import { Injectable } from '@gitlab/needle';
import { LogWriter } from '@gitlab-org/logging';
import { truncateToByteLimit } from '@gitlab-org/core';

/**
 * Writes every log line to stderr, regardless of level.
 *
 * Used by headless `run --output-format json`, where stdout is reserved for the
 * single machine-readable result document. Routing logs to stderr (rather than
 * a side-channel file) follows the Unix convention of result-on-stdout /
 * diagnostics-on-stderr, so a consumer can capture or discard logs with a plain
 * `2>` redirect while keeping stdout a clean JSON stream.
 */
@Injectable(LogWriter, [])
export class CliStderrLogWriter implements LogWriter {
  #broken = false;

  constructor() {
    // When stderr's pipe breaks (EPIPE — e.g. the editor that spawned
    // `duo acp` died), Bun/Node surface the write failure *asynchronously*:
    // without an 'error' listener it becomes an uncaughtException, whose
    // handler logs the error, which fails again — an unbounded recursion in
    // the microtask queue that starves timers and signal handlers (observed
    // as orphaned processes at ~150% CPU, multi-GB RSS, immune to SIGTERM).
    // Attaching a listener routes the failure here instead, where we latch
    // the stream as broken and stop logging.
    process.stderr.on('error', () => {
      this.#broken = true;
    });
  }

  write(msg: string): void {
    // Once stderr is gone it never comes back — skip the syscall (and the
    // exception it would raise) for every subsequent log line.
    if (this.#broken) return;

    const truncatedMsg = truncateToByteLimit(msg, 16384, { suffix: '... (truncated)' });
    try {
      process.stderr.write(`${truncatedMsg}\n`);
    } catch {
      // Synchronous variant of the write failure above: a log writer must
      // never throw. Drop the line and everything after it instead.
      this.#broken = true;
    }
  }
}
